import { useId } from 'react';
import type { TransactionExportFilters as ExportFilters } from '@mls-wallet/shared-types';
import { useAccounts } from '@/hooks/account/useAccounts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AccountSelector } from '@/components/ui/account-selector';

type Props = {
  filters: ExportFilters;
  onChange: (filters: ExportFilters) => void;
  error?: string;
  disabled?: boolean;
};

/** These values apply only to the CSV download, independently of Historic. */
export function TransactionExportFilters({
  filters,
  onChange,
  error,
  disabled = false,
}: Props) {
  const id = useId();
  const { accounts, isLoading } = useAccounts();

  const setFilter = (
    key: 'executedFrom' | 'executedThrough',
    value: string
  ) => {
    const next = { ...filters };
    if (value) next[key] = value;
    else delete next[key];
    onChange(next);
  };

  return (
    <section aria-label="CSV export filters" className="space-y-3">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor={`${id}-account`} className="text-xs">
            Accounts
          </Label>
          <AccountSelector
            id={`${id}-account`}
            accounts={accounts}
            selectedAccounts={accounts.filter((account) =>
              filters.accountIds?.includes(account.id)
            )}
            onSelectionChange={(selected) => {
              const next = { ...filters };
              if (selected.length)
                next.accountIds = selected.map(({ id }) => id);
              else delete next.accountIds;
              onChange(next);
            }}
            emptySelectionLabel="All accounts"
            disabled={disabled || isLoading}
            className="h-9 text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-from`} className="text-xs">
            Executed from (UTC)
          </Label>
          <Input
            id={`${id}-from`}
            type="date"
            value={filters.executedFrom ?? ''}
            onChange={(event) => setFilter('executedFrom', event.target.value)}
            disabled={disabled}
            className="block text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-through`} className="text-xs">
            Executed through (UTC)
          </Label>
          <Input
            id={`${id}-through`}
            type="date"
            value={filters.executedThrough ?? ''}
            onChange={(event) =>
              setFilter('executedThrough', event.target.value)
            }
            disabled={disabled}
            aria-invalid={!!error}
            aria-describedby={error ? `${id}-error` : undefined}
            className="block text-sm"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="justify-self-start"
          disabled={disabled || Object.keys(filters).length === 0}
          onClick={() => onChange({})}
        >
          Clear filters
        </Button>
      </div>
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Dates include the full end day. The CSV includes all matching token
          movements, including received tokens.
        </p>
      )}
    </section>
  );
}
